import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Search, MapPin, Globe, Facebook, ExternalLink, X, Trophy, Circle, ListOrdered, Flag, Calendar, Pencil, Trash2, Plus, Save } from 'lucide-react';

const TIER_META = {
  tier_1: { label: 'Dedicated',        color: '#22c55e', description: 'Official Crown Vic division, weekly points racing' },
  tier_2: { label: 'Eligible',         color: '#f59e0b', description: 'Enduro / Bomber / Full-Size Stock rulebook allows Crown Vic platform' },
  tier_3: { label: 'Special Events',   color: '#3b82f6', description: 'Crown Vics allowed in select events only' },
};

const US_CENTER = [39.5, -98.35];

function FitToMarkers({ tracks }) {
  const map = useMap();
  useEffect(() => {
    if (!tracks?.length) return;
    const valid = tracks.filter(t => t.lat && t.lng);
    if (valid.length === 0) return;
    const L = window.L || require('leaflet');
    const bounds = L.latLngBounds(valid.map(t => [t.lat, t.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 6 });
  }, [tracks, map]);
  return null;
}

export default function TracksMapPage() {
  const { user } = useAuth();
  const isAdmin = user && user.role === 'admin';
  const [tracks, setTracks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ state: '', surface: '', tier: '', frequency: '', search: '' });
  const [editing, setEditing] = useState(null); // track being edited or {} for new

  useEffect(() => {
    load();
    api.get('/tracks/stats').then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 300); // debounce
    return () => clearTimeout(t);
  }, [filters]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await api.get(`/tracks?${params.toString()}`);
      setTracks(res.data);
    } finally {
      setLoading(false);
    }
  };

  const filteredStates = useMemo(() => {
    const s = new Set(tracks.map((t) => t.state).filter(Boolean));
    return Array.from(s).sort();
  }, [tracks]);

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => setFilters({ state: '', surface: '', tier: '', frequency: '', search: '' });
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <div className="bg-[#141414] border-b border-[#262626]">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <p className="tag text-[#FF3B30] mb-2">National Intelligence Map</p>
              <h1 className="font-heading font-black text-4xl text-white">Crown Vic Track Atlas</h1>
              <p className="text-gray-400 text-sm mt-2 max-w-2xl">
                Every U.S. oval where Ford Crown Victoria platform cars are legally raced in 2026 — classified by how the rulebook treats them.
              </p>
            </div>
            {isAdmin && (
              <Button onClick={() => setEditing({ __new: true })} className="bg-[#FF3B30] hover:bg-[#E6352B]" data-testid="add-track-btn">
                <Plus className="w-4 h-4 mr-2" />Add Track
              </Button>
            )}
          </div>
          {stats && (
            <div className="flex items-center gap-4 mt-5 flex-wrap">
              <span className="inline-flex items-center gap-2 bg-[#1F1F1F] border border-[#262626] rounded px-3 py-1.5">
                <ListOrdered className="w-4 h-4 text-gray-400" />
                <span className="text-white font-bold">{stats.total}</span>
                <span className="text-gray-500 text-xs uppercase tracking-wider">tracks</span>
              </span>
              {Object.entries(TIER_META).map(([k, m]) => (
                <span key={k} className="inline-flex items-center gap-2 bg-[#1F1F1F] border border-[#262626] rounded px-3 py-1.5">
                  <Circle className="w-3 h-3" fill={m.color} color={m.color} />
                  <span className="text-white font-bold">{stats.by_tier?.[k] || 0}</span>
                  <span className="text-gray-500 text-xs uppercase tracking-wider">{m.label}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Filters + Map Layout */}
      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Filters */}
          <aside className="lg:col-span-3 space-y-4">
            <Card className="bg-[#141414] border-[#262626] p-4">
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  value={filters.search}
                  onChange={(e) => setFilter('search', e.target.value)}
                  placeholder="Search tracks, classes…"
                  className="pl-9 bg-[#1F1F1F] border-[#262626] text-white"
                  data-testid="tracks-search"
                />
              </div>
              <div className="space-y-3 text-sm">
                <FilterGroup label="Tier" value={filters.tier} onChange={(v) => setFilter('tier', v)} options={[
                  { value: '', label: 'All tiers' },
                  { value: 'tier_1', label: 'Dedicated', color: TIER_META.tier_1.color },
                  { value: 'tier_2', label: 'Eligible', color: TIER_META.tier_2.color },
                  { value: 'tier_3', label: 'Special Events', color: TIER_META.tier_3.color },
                ]} />
                <FilterGroup label="Surface" value={filters.surface} onChange={(v) => setFilter('surface', v)} options={[
                  { value: '', label: 'All surfaces' },
                  { value: 'asphalt', label: 'Asphalt' },
                  { value: 'dirt', label: 'Dirt' },
                  { value: 'both', label: 'Both' },
                ]} />
                <FilterGroup label="Frequency" value={filters.frequency} onChange={(v) => setFilter('frequency', v)} options={[
                  { value: '', label: 'Any' },
                  { value: 'weekly', label: 'Weekly' },
                  { value: 'monthly', label: 'Monthly' },
                  { value: 'special', label: 'Special' },
                ]} />
                <div>
                  <label className="text-gray-400 text-xs uppercase tracking-wider">State</label>
                  <select value={filters.state} onChange={(e) => setFilter('state', e.target.value)}
                    className="w-full mt-1 bg-[#1F1F1F] border border-[#262626] rounded px-2 py-1.5 text-white text-sm"
                    data-testid="state-filter">
                    <option value="">All states</option>
                    {filteredStates.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              {activeFilterCount > 0 && (
                <Button onClick={clearFilters} variant="outline" size="sm" className="w-full mt-3 border-[#262626] text-white">
                  Clear filters ({activeFilterCount})
                </Button>
              )}
            </Card>

            <Card className="bg-[#141414] border-[#262626] p-4">
              <p className="text-gray-500 text-xs uppercase tracking-wider mb-2">Tier Legend</p>
              <div className="space-y-2">
                {Object.entries(TIER_META).map(([k, m]) => (
                  <div key={k} className="flex items-start gap-2 text-xs">
                    <Circle className="w-3 h-3 mt-0.5 flex-shrink-0" fill={m.color} color={m.color} />
                    <div>
                      <p className="text-white font-bold">{m.label}</p>
                      <p className="text-gray-500 leading-snug">{m.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </aside>

          {/* Map */}
          <div className="lg:col-span-9">
            <Card className="bg-[#141414] border-[#262626] overflow-hidden">
              <div className="h-[520px] md:h-[640px] relative">
                <MapContainer
                  center={US_CENTER}
                  zoom={4}
                  scrollWheelZoom
                  style={{ height: '100%', width: '100%', background: '#0a0a0a' }}
                  data-testid="tracks-map"
                >
                  <TileLayer
                    url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>'
                  />
                  {tracks.filter((t) => t.lat && t.lng).map((t) => (
                    <CircleMarker
                      key={t.id}
                      center={[t.lat, t.lng]}
                      radius={t.tier === 'tier_1' ? 10 : 7}
                      pathOptions={{
                        color: TIER_META[t.tier]?.color || '#FF3B30',
                        fillColor: TIER_META[t.tier]?.color || '#FF3B30',
                        fillOpacity: 0.7,
                        weight: 2,
                      }}
                      eventHandlers={{ click: () => setSelected(t) }}
                    >
                      <Popup>
                        <div className="text-black">
                          <p className="font-bold">{t.name}</p>
                          <p className="text-xs">{t.city}, {t.state}</p>
                          <p className="text-xs text-red-600 uppercase mt-1">{TIER_META[t.tier]?.label}</p>
                          <button
                            className="text-xs text-red-600 underline mt-1"
                            onClick={() => setSelected(t)}
                          >
                            Open details →
                          </button>
                        </div>
                      </Popup>
                    </CircleMarker>
                  ))}
                </MapContainer>
                {loading && (
                  <div className="absolute top-4 right-4 bg-black/70 text-white text-xs px-3 py-1.5 rounded">Loading…</div>
                )}
                <div className="absolute bottom-4 left-4 bg-black/80 text-white text-xs px-3 py-1.5 rounded border border-[#262626]" data-testid="tracks-count">
                  {tracks.length} track{tracks.length !== 1 ? 's' : ''} shown
                </div>
              </div>
            </Card>

            {/* Track list below */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {tracks.slice(0, 12).map((t) => (
                <button
                  key={t.id}
                  onClick={() => setSelected(t)}
                  className="text-left bg-[#141414] border border-[#262626] hover:border-[#FF3B30] rounded p-3 transition-colors"
                  data-testid={`track-list-${t.id}`}
                >
                  <div className="flex items-start gap-2">
                    <Circle className="w-3 h-3 mt-1 flex-shrink-0"
                      fill={TIER_META[t.tier]?.color} color={TIER_META[t.tier]?.color} />
                    <div className="min-w-0 flex-1">
                      <p className="text-white font-bold truncate">{t.name}</p>
                      <p className="text-gray-500 text-xs">{t.city}, {t.state} · {t.surface}</p>
                      {t.class_names?.length > 0 && (
                        <p className="text-gray-400 text-xs mt-1 line-clamp-1">{t.class_names.join(' · ')}</p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
              {tracks.length > 12 && (
                <p className="text-gray-500 text-xs text-center py-2 md:col-span-2">
                  + {tracks.length - 12} more on the map
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Detail Drawer */}
      {selected && (
        <TrackDrawer
          track={selected}
          onClose={() => setSelected(null)}
          isAdmin={isAdmin}
          onEdit={() => { setEditing(selected); setSelected(null); }}
          onDelete={async () => {
            if (!window.confirm(`Delete "${selected.name}" from the atlas?`)) return;
            try {
              await api.delete(`/admin/tracks/${selected.id}`);
              setTracks((prev) => prev.filter((t) => t.id !== selected.id));
              setSelected(null);
            } catch (err) {
              alert(formatApiError(err));
            }
          }}
        />
      )}

      {editing && (
        <TrackEditDialog
          initial={editing.__new ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null);
            setTracks((prev) => {
              const exists = prev.some((t) => t.id === saved.id);
              if (exists) return prev.map((t) => (t.id === saved.id ? saved : t));
              return [saved, ...prev];
            });
            api.get('/tracks/stats').then((r) => setStats(r.data)).catch(() => {});
          }}
        />
      )}
    </div>
  );
}

function FilterGroup({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-gray-400 text-xs uppercase tracking-wider">{label}</label>
      <div className="flex flex-wrap gap-1.5 mt-1">
        {options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`text-xs px-2.5 py-1 rounded border transition-colors flex items-center gap-1 ${
              value === opt.value
                ? 'bg-[#FF3B30] border-[#FF3B30] text-white'
                : 'bg-[#1F1F1F] border-[#262626] text-gray-400 hover:text-white'
            }`}
          >
            {opt.color && <Circle className="w-2.5 h-2.5" fill={opt.color} color={opt.color} />}
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function TrackDrawer({ track, onClose, isAdmin, onEdit, onDelete }) {
  const tier = TIER_META[track.tier] || { label: 'Unknown', color: '#888' };
  return (
    <div className="fixed inset-0 z-[1100] flex">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <aside className="relative ml-auto w-full max-w-lg bg-[#141414] border-l border-[#262626] overflow-y-auto z-[1101]">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
        <div className="p-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider"
              style={{ backgroundColor: tier.color + '33', color: tier.color }}>
              <Circle className="w-2.5 h-2.5" fill={tier.color} color={tier.color} />
              Tier {track.tier?.split('_')[1]} · {tier.label}
            </span>
            <span className="tag bg-[#1F1F1F] text-gray-400 px-2 py-0.5 rounded text-[10px] uppercase">{track.surface}</span>
          </div>
          <h2 className="font-heading font-black text-2xl text-white leading-tight" data-testid="track-detail-name">{track.name}</h2>
          <p className="text-gray-400 text-sm mt-1 flex items-center gap-1">
            <MapPin className="w-3 h-3" />{track.city}, {track.state}
          </p>

          {isAdmin && (
            <div className="flex gap-2 mt-4 pb-4 border-b border-[#262626]">
              <Button onClick={onEdit} className="bg-[#FF3B30] hover:bg-[#E6352B] flex-1" data-testid="edit-track-btn">
                <Pencil className="w-4 h-4 mr-2" /> Edit this track
              </Button>
              <Button onClick={onDelete} variant="outline" className="border-[#262626] text-red-400 hover:text-red-300" data-testid="delete-track-btn">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}

          <div className="mt-6 space-y-4">
            <InfoBlock title="Crown Vic Status" icon={Trophy} content={(
              <>
                <p className="text-white text-sm font-bold capitalize">{track.crown_vic_status?.replace('_', ' ')}</p>
                <p className="text-gray-500 text-xs mt-0.5">{tier.description}</p>
              </>
            )} />
            {track.class_names?.length > 0 && (
              <InfoBlock title="Class Names" icon={Flag} content={(
                <div className="flex flex-wrap gap-1.5">
                  {track.class_names.map((c, i) => (
                    <span key={i} className="text-xs bg-[#1F1F1F] text-white border border-[#262626] rounded px-2 py-0.5">{c}</span>
                  ))}
                </div>
              )} />
            )}
            {track.rulebook_keywords?.length > 0 && (
              <InfoBlock title="Rulebook Keywords" content={(
                <div className="flex flex-wrap gap-1.5">
                  {track.rulebook_keywords.map((c, i) => (
                    <span key={i} className="text-xs bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/30 rounded px-2 py-0.5">
                      {c}
                    </span>
                  ))}
                </div>
              )} />
            )}
            <InfoBlock title="Race Frequency" icon={Calendar} content={<p className="text-white text-sm capitalize">{track.race_frequency || '—'}</p>} />

            {(track.website || track.facebook || track.rulebook_link) && (
              <InfoBlock title="Links" content={(
                <div className="space-y-2">
                  {track.website && (
                    <a href={track.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#FF3B30] hover:underline text-sm">
                      <Globe className="w-4 h-4" />Official Website <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  {track.facebook && (
                    <a href={track.facebook} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#FF3B30] hover:underline text-sm">
                      <Facebook className="w-4 h-4" />Facebook Page <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  {track.rulebook_link && (
                    <a href={track.rulebook_link} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#FF3B30] hover:underline text-sm">
                      <ListOrdered className="w-4 h-4" />Rulebook <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              )} />
            )}

            {track.notes && (
              <InfoBlock title="Notes" content={<p className="text-gray-300 text-sm leading-relaxed">{track.notes}</p>} />
            )}

            <p className="text-gray-600 text-xs pt-2 border-t border-[#262626]">
              Last verified: {track.last_verified || '—'}
            </p>
          </div>
        </div>
      </aside>
    </div>
  );
}

function InfoBlock({ title, icon: Icon, content }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        {Icon && <Icon className="w-3.5 h-3.5 text-gray-500" />}
        <p className="text-gray-500 text-xs uppercase tracking-wider">{title}</p>
      </div>
      {content}
    </div>
  );
}

function TrackEditDialog({ initial, onClose, onSaved }) {
  const isNew = !initial;
  const [form, setForm] = useState(() => ({
    id: initial?.id || '',
    name: initial?.name || '',
    city: initial?.city || '',
    state: initial?.state || '',
    surface: initial?.surface || 'asphalt',
    lat: initial?.lat ?? 0,
    lng: initial?.lng ?? 0,
    crown_vic_status: initial?.crown_vic_status || 'eligible',
    tier: initial?.tier || 'tier_2',
    class_names: (initial?.class_names || []).join(', '),
    rulebook_keywords: (initial?.rulebook_keywords || []).join(', '),
    race_frequency: initial?.race_frequency || 'weekly',
    website: initial?.website || '',
    facebook: initial?.facebook || '',
    rulebook_link: initial?.rulebook_link || '',
    notes: initial?.notes || '',
    last_verified: initial?.last_verified || new Date().toISOString().slice(0, 10),
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        lat: parseFloat(form.lat) || 0,
        lng: parseFloat(form.lng) || 0,
        class_names: form.class_names.split(',').map((s) => s.trim()).filter(Boolean),
        rulebook_keywords: form.rulebook_keywords.split(',').map((s) => s.trim()).filter(Boolean),
      };
      let saved;
      if (isNew) {
        const res = await api.post('/admin/tracks', payload);
        saved = res.data;
      } else {
        const res = await api.put(`/admin/tracks/${initial.id}`, payload);
        saved = res.data;
      }
      onSaved(saved);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{isNew ? 'Add Track' : `Edit: ${initial.name}`}</DialogTitle>
        </DialogHeader>
        {error && <div className="bg-red-500/10 border border-red-500/40 text-red-400 px-3 py-2 rounded text-sm">{error}</div>}
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>Track Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
                className="bg-[#1F1F1F] border-[#262626] text-white" data-testid="track-form-name" />
            </div>
            <div>
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="bg-[#1F1F1F] border-[#262626] text-white" />
            </div>
            <div>
              <Label>State (2-letter)</Label>
              <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase().slice(0, 2) })}
                className="bg-[#1F1F1F] border-[#262626] text-white" />
            </div>
            <div>
              <Label>Latitude</Label>
              <Input type="number" step="0.0001" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })}
                className="bg-[#1F1F1F] border-[#262626] text-white" />
            </div>
            <div>
              <Label>Longitude</Label>
              <Input type="number" step="0.0001" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })}
                className="bg-[#1F1F1F] border-[#262626] text-white" />
            </div>
            <div>
              <Label>Tier</Label>
              <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })}
                className="w-full mt-1 bg-[#1F1F1F] border border-[#262626] rounded px-2 py-2 text-white text-sm">
                <option value="tier_1">Tier 1 — Dedicated</option>
                <option value="tier_2">Tier 2 — Eligible</option>
                <option value="tier_3">Tier 3 — Special Events</option>
              </select>
            </div>
            <div>
              <Label>Crown Vic Status</Label>
              <select value={form.crown_vic_status} onChange={(e) => setForm({ ...form, crown_vic_status: e.target.value })}
                className="w-full mt-1 bg-[#1F1F1F] border border-[#262626] rounded px-2 py-2 text-white text-sm">
                <option value="dedicated">Dedicated</option>
                <option value="eligible">Eligible</option>
                <option value="special_events">Special Events</option>
              </select>
            </div>
            <div>
              <Label>Surface</Label>
              <select value={form.surface} onChange={(e) => setForm({ ...form, surface: e.target.value })}
                className="w-full mt-1 bg-[#1F1F1F] border border-[#262626] rounded px-2 py-2 text-white text-sm">
                <option value="asphalt">Asphalt</option>
                <option value="dirt">Dirt</option>
                <option value="both">Both</option>
              </select>
            </div>
            <div>
              <Label>Frequency</Label>
              <select value={form.race_frequency} onChange={(e) => setForm({ ...form, race_frequency: e.target.value })}
                className="w-full mt-1 bg-[#1F1F1F] border border-[#262626] rounded px-2 py-2 text-white text-sm">
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="special">Special</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <Label>Class Names (comma-separated)</Label>
              <Input value={form.class_names} onChange={(e) => setForm({ ...form, class_names: e.target.value })}
                placeholder="Crown Vic, Bomber, Enduro" className="bg-[#1F1F1F] border-[#262626] text-white" />
            </div>
            <div className="md:col-span-2">
              <Label>Rulebook Keywords (comma-separated)</Label>
              <Input value={form.rulebook_keywords} onChange={(e) => setForm({ ...form, rulebook_keywords: e.target.value })}
                placeholder="Crown Victoria, OEM V8, Panther platform" className="bg-[#1F1F1F] border-[#262626] text-white" />
            </div>
            <div>
              <Label>Website</Label>
              <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
                placeholder="https://…" className="bg-[#1F1F1F] border-[#262626] text-white" />
            </div>
            <div>
              <Label>Facebook</Label>
              <Input value={form.facebook} onChange={(e) => setForm({ ...form, facebook: e.target.value })}
                placeholder="https://facebook.com/…" className="bg-[#1F1F1F] border-[#262626] text-white" />
            </div>
            <div className="md:col-span-2">
              <Label>Rulebook Link (PDF / page)</Label>
              <Input value={form.rulebook_link} onChange={(e) => setForm({ ...form, rulebook_link: e.target.value })}
                placeholder="https://…/rules.pdf" className="bg-[#1F1F1F] border-[#262626] text-white" />
            </div>
            <div className="md:col-span-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3} className="bg-[#1F1F1F] border-[#262626] text-white" />
            </div>
            <div>
              <Label>Last Verified (YYYY-MM-DD)</Label>
              <Input value={form.last_verified} onChange={(e) => setForm({ ...form, last_verified: e.target.value })}
                className="bg-[#1F1F1F] border-[#262626] text-white" />
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={saving} className="bg-[#FF3B30] hover:bg-[#E6352B]" data-testid="save-track-btn">
              <Save className="w-4 h-4 mr-2" />{saving ? 'Saving…' : (isNew ? 'Create Track' : 'Save Changes')}
            </Button>
            <Button type="button" onClick={onClose} variant="outline" className="border-[#262626] text-white">
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
